import { useRef, useCallback, useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import useCommonToast from '../../hooks/useCommonToast';
import {
    getBasisDartSpread,
    getValidDartSpreadRows,
    modifyDartSpread,
    deleteAllDartSpreadData,
    columnSchemaOnDartSpreadGrid,
    validateParsedNewRowsOnDartSpreadGrid,
    addDartSpreadRows,
} from '../../services/basis';
import { Flex, Box, Spacer, SimpleGrid, FormLabel } from '@chakra-ui/react';

import { isNumberCellEmpty } from '../../services/items';
import useTabularView from '../../hooks/useTabularView';

import Combobox from '../forms/Combobox';

import DeleteAllButton from '../itemData/DeleteAllButton';
import AddRowsButton from '../itemData/AddRowsButton';

import DataGridWrapper from '../grid/DataGridWrapper';
import DataGrid from '../grid/DataGrid';

const DartSpreadTab = ({ options, basis, basisList, updateBasis, onSortingChange, newDataImported }) => {
    const dataTabularView = useTabularView('nope_i_dont_care_about_this', NaN);

    const [daHourlyId, setDaHourlyId] = useState(basis.daHourlyBasisId);
    const [scalingIntervalId, setScalingIntervalId] = useState(basis.scalingintervalId);

    const gridApi = useRef(null);
    const intl = useIntl();

    const toast = useCommonToast();

    useEffect(() => {
        if (gridApi.current) {
            gridApi.current.refreshServerSideStore({
                purge: true,
            });
        }
    }, [newDataImported]);

    const onGridReady = (params) => {
        gridApi.current = params.api;
    };

    const getServerSideData = useCallback(
        async (params) => {
            const page = params.request.startRow / 500 + 1;
            const sortColumn = params.request.sortModel[0] || null;
            const orderBy = sortColumn ? sortColumn.colId : null;
            const orderByDescending = sortColumn ? sortColumn.sort === 'desc' : true;

            const payload = {
                page,
                elementsPerPage: 500,
                sortColumn: orderBy,
                sortByDescending: orderByDescending,
            };

            const data = await getBasisDartSpread(basis.basisId, payload);

            onSortingChange({
                sortColumn: orderBy,
                sortByDescending: orderByDescending,
            });

            if (!data.totalCount) {
                params.api.showNoRowsOverlay();
            } else {
                params.api.hideOverlay();
            }

            params.success({
                rowData: data.items,
                rowCount: data.totalCount,
            });
        },
        [basis.basisId, onSortingChange]
    );

    const gridColumns = [
        {
            field: 'month',
            type: 'number',
            cellEditorParams: { required: false },
            headerName: intl.formatMessage({ id: 'basis_tabs_month' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'year',
            type: 'number',
            cellEditorParams: { required: false },
            headerName: intl.formatMessage({ id: 'basis_tabs_year' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'peakPeriodLabelId',
            type: 'select',
            cellEditorParams: { options: options.peakPeriods, required: false },
            headerName: intl.formatMessage({ id: 'basis_tabs_peak_period' }),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'multiplier',
            type: 'number',
            cellEditorParams: { required: false },
            headerName: intl.formatMessage({ id: 'basis_tabs_multiplier' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'adder',
            type: 'number',
            cellEditorParams: { required: false },
            headerName: intl.formatMessage({ id: 'basis_tabs_adder' }),
            resizable: true,
            sortable: true,
        },
    ];

    const addCols = gridColumns.map((c) => ({
        field: c.field,
        headerName: c.headerName,
    }));

    const updateDartSpreadData = async (rowIds) => {
        const rows = rowIds.map((id) => gridApi.current.getRowNode(id));

        const validRows = getValidDartSpreadRows(rows);

        if (validRows.length === 0) return;

        try {
            await modifyDartSpread(validRows);

            toast(intl.formatMessage({ id: 'basis_save_changes_success' }));
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    const formatRows = (parsedData) => {
        return parsedData.map((row) => {
            return {
                ...row,

                peakPeriodLabelId: options.peakPeriods.find(({ description }) => description === row.peakPeriodLabelId)
                    ?.id,

                month: isNumberCellEmpty(row.month),
                year: isNumberCellEmpty(row.year),
                multiplier: isNumberCellEmpty(row.multiplier),
                adder: isNumberCellEmpty(row.adder),
            };
        });
    };

    const handleSelectChange = async (value, type) => {
        const tempBasis = { ...basis };
        if (type === 'daHourly') {
            setDaHourlyId(value);
            tempBasis.daHourlyBasisId = value;
        }
        if (type === 'scaling') {
            setScalingIntervalId(value);
            tempBasis.scalingintervalId = value;
        }

        updateBasis(tempBasis);
    };

    const onAddRows = async (newRows) => {
        const { setIsLoading, addRows } = dataTabularView;

        const successMessage = intl.formatMessage({ id: 'common_generic_item_change_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);

            await addDartSpreadRows(basis.basisId, newRows);

            gridApi.current.refreshServerSideStore({
                purge: true,
            });

            setIsLoading(false);
            addRows.onClose();

            toast(successMessage);
        } catch (error) {
            setIsLoading(false);

            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    return (
        <Box>
            <SimpleGrid pb={5} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                <Box>
                    <FormLabel>
                        <FormattedMessage id="basis_tabs_scaling_interval" />
                    </FormLabel>
                    <Combobox
                        mb={3}
                        options={options.scalingIntervals}
                        valueKey="id"
                        labelKey="description"
                        value={scalingIntervalId}
                        onChange={(value) => handleSelectChange(value, 'scaling')}
                    />
                </Box>
                <Box>
                    <FormLabel>
                        <FormattedMessage id="basis_tabs_da_houly_basis" />
                    </FormLabel>
                    <Combobox
                        mb={3}
                        options={basisList}
                        valueKey="id"
                        labelKey="description"
                        value={daHourlyId}
                        onChange={(value) => handleSelectChange(value, 'daHourly')}
                    />
                </Box>
            </SimpleGrid>
            <Flex mb={6} direction={{ base: 'column', xl: 'row' }}>
                <Spacer />
                <DeleteAllButton
                    subItemId={basis.basisId}
                    deleteCallback={deleteAllDartSpreadData}
                    successCallback={() => {}}
                    refetchData={() => {
                        gridApi.current.refreshServerSideStore({
                            purge: true,
                        });
                    }}
                    isInTabularTab={true}
                />

                <AddRowsButton
                    modal={dataTabularView.addRows}
                    isLoading={dataTabularView.isLoading}
                    disabled={false}
                    tabIndex={0}
                    validate={validateParsedNewRowsOnDartSpreadGrid}
                    format={formatRows}
                    columns={addCols}
                    columnSchema={{
                        ...columnSchemaOnDartSpreadGrid,
                        peakPeriodLabelId: {
                            ...columnSchemaOnDartSpreadGrid.peakPeriodLabelId,
                            constraints: options.peakPeriods.map((elem) => elem.description),
                        },
                    }}
                    addRows={onAddRows}
                />
            </Flex>
            <DataGridWrapper>
                <DataGrid
                    isInitialRequestSent={true}
                    onGridReady={onGridReady}
                    enableBrowserTooltips={true}
                    serverSideDatasource={{ getRows: getServerSideData }}
                    onDataChange={updateDartSpreadData}
                    noDataMessageId="basis_no_data"
                    paginationPageSize={500}
                    cacheBlockSize={500}
                    rowModelType="serverSide"
                    serverSideStoreType="partial"
                    columns={gridColumns}
                />
            </DataGridWrapper>
        </Box>
    );
};

export default DartSpreadTab;
